import moment from 'moment/moment'
import * as React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { getGroupList } from 'slices/groupsSlice'
import { selectSessionStatus } from 'slices/sessionSlice'
import { getSkillList } from 'slices/skillsSlice'
import { getTenant } from 'slices/tenantsSlice'
import { getWorkByDate, selectWorksStatus } from 'slices/worksSlice'

import { roundedMoment } from 'components/common/utils'

import useBusinessTime from './useBusinessTime'
import useQuery from './useQuery'
import useWorkNotification from './useWorkNotification'

import type { Moment } from 'moment/moment'

const useAssignment = () => {
  const [startHour, setStartHour] = React.useState<string>(roundedMoment(false).hour)
  const [startMinute, setStartMinute] = React.useState<string>(roundedMoment(false).minute)

  const dispatch = useDispatch()
  const params = useParams<'workspaceId'>()

  const { user, team } = useSelector(selectSessionStatus, shallowEqual)
  const { works } = useSelector(selectWorksStatus, shallowEqual)

  const query = useQuery()
  const { setSubmitted } = useWorkNotification()

  const [openMoment, setOpenMoment] = React.useState<Moment>(moment())

  const workspaceId = React.useMemo(() => {
    return Number(params.workspaceId) || team.workspaceId
  }, [params, team])

  const { businessStartTime, businessEndTime } = useBusinessTime()
  const work = React.useMemo(() => works.find(w => w.date === moment().format('YYYY-MM-DD')), [works])

  React.useEffect(() => {
    const qHour = query.get('hour')
    const qMinute = query.get('minute')

    if (!qHour || !qMinute) {
      const { hour, minute } = roundedMoment(false)
      setStartHour(hour)
      setStartMinute(minute)
      return
    }
    setStartHour(qHour)
    setStartMinute(qMinute)
    // クエリがない時、TimeSelectの時刻を15分間隔で更新するためにopenMomentを追加
  }, [query, openMoment])

  React.useEffect(() => {
    dispatch(getSkillList())
  }, [dispatch])

  React.useEffect(() => {
    dispatch(getTenant(user.tenants[0].tenantId))
  }, [dispatch, user])

  React.useEffect(() => {
    if (!workspaceId) {
      return
    }
    dispatch(getWorkByDate(workspaceId, moment().format('YYYY-MM-DD'), false, true))
    dispatch(getGroupList(workspaceId))
  }, [dispatch, workspaceId])

  React.useEffect(() => {
    if (startMinute && startHour) {
      setOpenMoment(moment(`${startHour}:${startMinute}`, 'HH:mm'))
    }
  }, [startHour, startMinute])

  const isBetween = React.useCallback(
    (startAt: string, duration: number): boolean => {
      const start = moment(startAt).local().format()
      const end = moment(startAt).add(duration, 'seconds').local().format()

      return openMoment.isBetween(start, end, 'minute', '[)')
    },
    [openMoment]
  )

  React.useEffect(() => {
    if (!workspaceId) {
      return
    }

    // 15 分毎に openMoment を書き換えることで API を定期的に呼ぶ
    const timerId = setTimeout(
      () => {
        dispatch(getWorkByDate(workspaceId, moment().format('YYYY-MM-DD'), false, true))
        setOpenMoment(moment(`${startHour}:${startMinute}`, 'HH:mm'))
      },
      15 * 60 * 1000
    )
    return () => clearTimeout(timerId)
  }, [dispatch, workspaceId, openMoment, startHour, startMinute])

  return {
    startHour,
    startMinute,
    openMoment,
    businessStartTime,
    businessEndTime,
    workspaceId,
    work,
    isBetween,
    setSubmitted,
  }
}

export default useAssignment
